export * from './admin-operations.js';
import { z } from 'zod';
export * from './storefront-design.js';
export * from './public-page-design.js';
export * from './link-page-options.js';
export * from './platform-directory.js';
export * from './platform-overview.js';
export * from './platform-billing.js';
export * from './platform-traffic.js';
export { createLocalizedTextSchema, resolveLocalizedText } from './merchant-content.js';
export const localeSchema = z.enum(['de', 'en', 'nl', 'ar']);
export const customerProfileInputSchema = z.object({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    email: z.string().trim().max(254).pipe(z.email()).transform((value) => value.toLowerCase()),
    locale: localeSchema,
}).strict();
export function staffInvitationPath(token) {
    if (!/^[A-Za-z0-9_-]{40,128}$/.test(token))
        throw new Error('Staff invitation token is malformed.');
    return `/team/invite#invite=${encodeURIComponent(token)}`;
}
export const customerDocumentKindSchema = z.enum(['invoice', 'receipt', 'credit_note']);
export const organizationDocumentProfileSchema = z.object({
    legalName: z.string().trim().min(2).max(200),
    addressLine1: z.string().trim().max(200).nullable().default(null),
    addressLine2: z.string().trim().max(200).nullable().default(null),
    city: z.string().trim().max(120).nullable().default(null),
    postalCode: z.string().trim().max(30).nullable().default(null),
    countryCode: z.string().trim().length(2).transform((value) => value.toUpperCase()),
    taxNumber: z.string().trim().max(80).nullable().default(null),
    vatNumber: z.string().trim().max(80).nullable().default(null),
    commercialRegistration: z.string().trim().max(100).nullable().default(null),
    issuerEmail: z.email().nullable().default(null),
    invoicePrefix: z.string().trim().regex(/^[A-Z0-9-]{2,12}$/),
    receiptPrefix: z.string().trim().regex(/^[A-Z0-9-]{2,12}$/),
    creditNotePrefix: z.string().trim().regex(/^[A-Z0-9-]{2,12}$/),
    defaultLocale: localeSchema,
});
const nullableInstruction = z.string().trim().max(500).nullable().default(null);
export const offlinePaymentSettingsSchema = z.object({
    bankTransferEnabled: z.boolean(),
    accountHolder: z.string().trim().min(2).max(160).nullable().default(null),
    bankName: z.string().trim().min(2).max(160).nullable().default(null),
    iban: z.string().trim().max(42).nullable().transform((value) => value ? value.replace(/\s+/g, '').toUpperCase() : null),
    bic: z.string().trim().max(14).nullable().transform((value) => value ? value.replace(/\s+/g, '').toUpperCase() : null),
    instructions: z.object({
        de: nullableInstruction, en: nullableInstruction, nl: nullableInstruction, ar: nullableInstruction,
    }),
}).superRefine((value, context) => {
    if (!value.bankTransferEnabled)
        return;
    if (!value.accountHolder)
        context.addIssue({ code: 'custom', path: ['accountHolder'], message: 'Account holder is required.' });
    if (!value.bankName)
        context.addIssue({ code: 'custom', path: ['bankName'], message: 'Bank name is required.' });
    if (!value.iban || !/^[A-Z]{2}[A-Z0-9]{13,32}$/.test(value.iban)) {
        context.addIssue({ code: 'custom', path: ['iban'], message: 'A valid IBAN is required.' });
    }
    if (value.bic && !/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(value.bic)) {
        context.addIssue({ code: 'custom', path: ['bic'], message: 'BIC must contain 8 or 11 letters and digits.' });
    }
});
export const bookingStatusSchema = z.enum([
    'hold',
    'pending_payment',
    'confirmed',
    'cancelled',
    'completed',
    'no_show',
]);
export const paymentStateSchema = z.enum([
    'pending',
    'authorized',
    'paid',
    'partially_refunded',
    'refunded',
    'failed',
    'cancelled',
    'disputed',
]);
export const publicPaymentPolicySchema = z.enum([
    'full',
    'fixed_deposit',
    'percentage_deposit',
    'pay_later',
    'member_credit',
]);
export const durationOptionPriceModeSchema = z.enum(['included', 'adjustment', 'fixed_total']);
export const serviceDurationOptionInputSchema = z.object({
    id: z.uuid().optional(),
    label: z.string().trim().min(1).max(100),
    durationMinutes: z.number().int().min(5).max(720).refine((value) => value % 5 === 0, 'Duration must use five-minute increments.'),
    priceMode: durationOptionPriceModeSchema.default('adjustment'),
    priceCents: z.number().int().min(0),
    minNoticeMinutes: z.number().int().min(0).nullable().optional(),
    active: z.boolean().default(true),
});
export const feedbackDisplayNameSchema = z.enum(['full_name', 'first_name', 'anonymous']);
export const feedbackSubmissionSchema = z.object({
    rating: z.number().int().min(1).max(5),
    reviewText: z.string().trim().max(2000).optional().default(''),
    privateNote: z.string().trim().max(2000).optional().default(''),
    publicationConsent: z.boolean().default(false),
    displayNamePreference: feedbackDisplayNameSchema.default('first_name'),
    locale: localeSchema.default('en'),
}).refine((value) => value.rating > 0 || value.reviewText.length > 0 || value.privateNote.length > 0, {
    message: 'Feedback cannot be empty.',
});
export const localizedStorefrontCopySchema = z.object({
    de: z.string().trim().max(240).optional(),
    en: z.string().trim().max(240).optional(),
    nl: z.string().trim().max(240).optional(),
    ar: z.string().trim().max(240).optional(),
}).default({});
const storefrontSectionBase = {
    key: z.string().regex(/^[a-z][a-z0-9_-]{1,39}$/),
    enabled: z.boolean().default(true),
    heading: localizedStorefrontCopySchema,
    body: localizedStorefrontCopySchema,
};
export const storefrontSectionSchema = z.discriminatedUnion('type', [
    z.object({ ...storefrontSectionBase, type: z.literal('hero'), variant: z.enum(['service_first', 'image_first', 'availability_first']) }),
    z.object({ ...storefrontSectionBase, type: z.literal('services'), variant: z.enum(['list', 'cards', 'categories']) }),
    z.object({ ...storefrontSectionBase, type: z.literal('resources'), variant: z.enum(['compact', 'profiles', 'availability']) }),
    z.object({ ...storefrontSectionBase, type: z.literal('testimonials'), variant: z.enum(['quotes', 'rating_cards', 'featured']) }),
    z.object({ ...storefrontSectionBase, type: z.literal('social'), variant: z.enum(['strip', 'profile_cards', 'tiles']) }),
    z.object({ ...storefrontSectionBase, type: z.literal('content_cards'), variant: z.enum(['simple', 'features', 'media']) }),
    z.object({ ...storefrontSectionBase, type: z.literal('contact'), variant: z.enum(['compact', 'split', 'map']) }),
    z.object({ ...storefrontSectionBase, type: z.literal('booking_cta'), variant: z.enum(['sticky', 'inline', 'service_specific']) }),
]);
export const storefrontSectionsSchema = z.array(storefrontSectionSchema).min(2).max(20).superRefine((sections, context) => {
    const keys = new Set();
    const types = new Set();
    sections.forEach((section, index) => {
        if (keys.has(section.key))
            context.addIssue({ code: 'custom', path: [index, 'key'], message: 'Section keys must be unique.' });
        if (types.has(section.type))
            context.addIssue({ code: 'custom', path: [index, 'type'], message: 'Each section type can appear only once.' });
        keys.add(section.key);
        types.add(section.type);
    });
    for (const required of ['hero', 'booking_cta']) {
        if (!sections.some((section) => section.type === required && section.enabled)) {
            context.addIssue({ code: 'custom', message: `An enabled ${required} section is required.` });
        }
    }
});
const storefrontSection = (section) => section;
export function storefrontTemplateSections(template) {
    const shared = {
        testimonials: storefrontSection({ key: 'testimonials', type: 'testimonials', enabled: true, variant: 'rating_cards', heading: {}, body: {} }),
        social: storefrontSection({ key: 'social', type: 'social', enabled: true, variant: 'strip', heading: {}, body: {} }),
        content: storefrontSection({ key: 'content', type: 'content_cards', enabled: true, variant: 'simple', heading: {}, body: {} }),
        contact: storefrontSection({ key: 'contact', type: 'contact', enabled: true, variant: 'split', heading: {}, body: {} }),
        booking: storefrontSection({ key: 'booking', type: 'booking_cta', enabled: true, variant: 'sticky', heading: {}, body: {} }),
    };
    if (template === 'portfolio')
        return [
            storefrontSection({ key: 'hero', type: 'hero', enabled: true, variant: 'image_first', heading: {}, body: {} }),
            storefrontSection({ key: 'services', type: 'services', enabled: true, variant: 'cards', heading: {}, body: {} }),
            storefrontSection({ key: 'resources', type: 'resources', enabled: true, variant: 'profiles', heading: {}, body: {} }),
            { ...shared.testimonials, variant: 'featured' },
            { ...shared.content, variant: 'media' },
            { ...shared.social, variant: 'tiles' },
            shared.contact,
            { ...shared.booking, variant: 'service_specific' },
        ];
    if (template === 'wellness')
        return [
            storefrontSection({ key: 'hero', type: 'hero', enabled: true, variant: 'service_first', heading: {}, body: {} }),
            storefrontSection({ key: 'services', type: 'services', enabled: true, variant: 'list', heading: {}, body: {} }),
            storefrontSection({ key: 'resources', type: 'resources', enabled: true, variant: 'profiles', heading: {}, body: {} }),
            shared.content,
            { ...shared.testimonials, variant: 'quotes' },
            { ...shared.contact, variant: 'compact' },
            { ...shared.booking, variant: 'inline' },
            shared.social,
        ];
    return [
        storefrontSection({ key: 'hero', type: 'hero', enabled: true, variant: 'availability_first', heading: {}, body: {} }),
        storefrontSection({ key: 'services', type: 'services', enabled: true, variant: 'categories', heading: {}, body: {} }),
        storefrontSection({ key: 'resources', type: 'resources', enabled: true, variant: 'compact', heading: {}, body: {} }),
        shared.testimonials,
        shared.social,
        { ...shared.content, variant: 'features' },
        shared.contact,
        shared.booking,
    ];
}
export function calculateDurationOptionPrice(basePriceCents, priceMode, optionPriceCents) {
    if (priceMode === 'fixed_total')
        return optionPriceCents;
    if (priceMode === 'adjustment')
        return basePriceCents + optionPriceCents;
    return basePriceCents;
}
export function calculatePaymentSchedule(totalCents, dueNowCents, paidCents = 0, refundedCents = 0) {
    const netPaidCents = Math.max(0, paidCents - refundedCents);
    return {
        totalCents,
        dueNowCents,
        paidCents,
        refundedCents,
        outstandingCents: Math.max(0, totalCents - netPaidCents),
    };
}
export function calculateDueNowCents(priceCents, policy, depositValue) {
    if (policy === 'pay_later')
        return 0;
    if (policy === 'fixed_deposit')
        return Math.min(priceCents, Math.max(0, depositValue));
    if (policy === 'percentage_deposit')
        return Math.round((priceCents * Math.min(100, Math.max(0, depositValue))) / 100);
    return priceCents;
}
export const availabilityQuerySchema = z.object({
    date: z.iso.date(),
    players: z.coerce.number().int().min(1).max(24).default(1),
    locale: localeSchema.optional(),
    showUnavailable: z.union([
        z.boolean(),
        z.enum(['true', 'false']).transform((value) => value === 'true'),
    ]).default(false),
    serviceId: z.uuid().optional(),
    durationOptionId: z.uuid().optional(),
});
export const createHoldSchema = z.object({
    resourceId: z.uuid(),
    serviceId: z.uuid().nullable().optional(),
    durationOptionId: z.uuid().nullable().optional(),
    startsAt: z.iso.datetime({ offset: true }),
    endsAt: z.iso.datetime({ offset: true }),
    players: z.number().int().min(1).max(24),
    locale: localeSchema.default('de'),
});
export const completeBookingSchema = z.object({
    holdToken: z.string().min(16),
    locale: localeSchema.default('de'),
    customer: z.object({
        firstName: z.string().trim().min(1).max(80),
        lastName: z.string().trim().min(1).max(80),
        email: z.email(),
        phone: z.union([z.literal(''), z.string().trim().min(7).max(30)]).optional(),
        isMember: z.boolean().default(false),
        membershipNumber: z.string().trim().max(80).optional(),
    }).superRefine((customer, context) => {
        if (customer.isMember && !customer.membershipNumber) {
            context.addIssue({ code: 'custom', message: 'Membership number is required.', path: ['membershipNumber'] });
        }
    }),
    paymentMode: z.enum(['full', 'deposit', 'pay_later', 'member_credit']).default('deposit'),
    acceptTerms: z.literal(true),
    termsVersionId: z.uuid(),
    termsContentHash: z.string().regex(/^[a-f0-9]{64}$/),
});
export const cancelBookingSchema = z.object({
    token: z.string().min(16),
    reason: z.string().trim().min(2).max(500),
});
export const bookingLookupSchema = z.object({
    bookingCode: z.string().trim().regex(/^(?:SEA-\d{6}-[0-9A-HJKMNP-TV-Z]{5}-[0-9A-HJKMNP-TV-Z]{5}|SF-\d{6}-[A-F0-9]{20})$/i, 'Invalid booking reference.')
        .transform((value) => value.toUpperCase()),
    email: z.email().trim().transform((value) => value.toLowerCase()),
});
export const accountBookingClaimSchema = z.object({
    bookingCode: z.string().trim().min(8).max(40).transform((value) => value.toUpperCase()),
    email: z.string().trim().email().transform((value) => value.toLowerCase()),
});
export const refundSchema = z.object({
    amountCents: z.number().int().positive(),
    reason: z.enum(['customer_request', 'venue_closure', 'duplicate', 'other']),
    note: z.string().trim().max(500).optional(),
});
export function calculateCancellationDeadline(startsAt, cancellationHours) {
    const startsAtMs = typeof startsAt === 'string' ? Date.parse(startsAt) : startsAt.getTime();
    return new Date(startsAtMs - Math.max(15, cancellationHours * 60) * 60_000).toISOString();
}
export * from './product-updates.js';
//# sourceMappingURL=index.js.map